import { theme } from '../../../helpers/Theme';

import {
  MenuItemProps,
  type MenuItemContentProps,
  IMenuProps,
  VARIANT,
} from './Menu.typed';

import * as Styled from './Menu.styled';

import { LINK_TYPE } from '../Button/SharedLink';

const MenuItemContent = (props: MenuItemContentProps) => {
  const {
    icons,
    item: { icon, displayName, notifications, newTabIcon, betaIcon },
  } = props;

  return (
    <>
      {icons && icon && <Styled.ImageContainer>{icon}</Styled.ImageContainer>}
      {displayName}
      {notifications && <Styled.StyledBadge count={notifications} />}
      {newTabIcon && (
        <Styled.OpenIcon
          color={theme.colors.BLUE_DARK}
          width="12"
          height="12"
        />
      )}
      {betaIcon && (
        <Styled.StyledTag
          text="Beta"
          textColor="GREY_DARK"
          bgColor="GREY_LIGHTER"
        />
      )}
    </>
  );
};

const MenuItem = (props: MenuItemProps) => {
  const { item, icons, variant } = props;
  const {
    divider = false,
    selected = false,
    displayNextLinkAsShallow,
    id,
    link,
    onClick,
    dataTracking,
    displayName,
    color = 'GREY_DARKER',
  } = item;

  const includesLink = typeof item.link === 'string';

  const commonProps = {
    variant,
    ofType: LINK_TYPE.SECONDARY,
    onClick,
    selected,
    'data-tracking': dataTracking,
    'data-testid': `menu-item-${displayName
      .replace(/\s+/g, '-')
      .toLowerCase()}`,
    color,
  };

  return (
    <Styled.Item
      as="div"
      divider={divider}
      selected={selected}
      data-testid={selected ? 'menu-item-selected' : `menu-item-${id}`}
    >
      {includesLink && (
        <Styled.StyledLink
          {...commonProps}
          shallow={displayNextLinkAsShallow}
          href={link}
        >
          <MenuItemContent item={item} icons={icons} />
        </Styled.StyledLink>
      )}
      {!includesLink && (
        <Styled.StyledLinkButton {...commonProps}>
          <MenuItemContent item={item} icons={icons} />
        </Styled.StyledLinkButton>
      )}
    </Styled.Item>
  );
};

const Menu = (props: IMenuProps) => {
  const {
    items,
    icons = false,
    variant,
    className,
    selectedSection,
    'data-testid': dataTestId,
  } = props;

  const selectedIndex = items.findIndex((tab) => {
    if (Array.isArray(tab)) {
      return tab[0].id === selectedSection;
    } else return tab.id === selectedSection;
  });

  return (
    <Styled.Container className={className} data-testid={dataTestId}>
      {items.map((item, index) => {
        if (Array.isArray(item)) {
          return (
            <Styled.NestedItem key={`item-${index}`}>
              <Styled.NestedContainer>
                {item.map((item, index) => (
                  <>
                    {index !== 0 && <span>/</span>}
                    <MenuItem
                      item={item}
                      icons={icons}
                      key={`nested-${index}`}
                    />
                  </>
                ))}
              </Styled.NestedContainer>
            </Styled.NestedItem>
          );
        }
        return (
          <MenuItem item={item} icons={icons} key={index} variant={variant} />
        );
      })}
      {selectedSection &&
      (variant === VARIANT.DASHBOARD || variant === VARIANT.DEALERHUB) ? (
        <Styled.Slider index={selectedIndex} />
      ) : undefined}
    </Styled.Container>
  );
};

export { Menu };
